// Dashboard.js
import { useEffect, useMemo, useState } from "react";
import {
  Grid,
  Typography,
  Box,
  Alert,
  Chip,
  Paper,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  useMediaQuery,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import api from "../api/api";
import { CircularProgressbarWithChildren, buildStyles } from "react-circular-progressbar";
import { Line } from "react-chartjs-2";
import "react-circular-progressbar/dist/styles.css";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  CategoryScale,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Title, ChartTooltip, Legend);

function formatValue(value, casas = 2) {
  if (typeof value === "number") return value.toFixed(casas).replace(".", ",");
  return value;
}

function ajustarHoraBrasilia(dt) {
  if (!(dt instanceof Date)) dt = new Date(dt);
  return new Date(dt.getTime() - 0 * 60 * 60 * 1000);
}

function mean(arr) {
  const v = (arr || []).filter((n) => Number.isFinite(n));
  if (!v.length) return undefined;
  return v.reduce((a, b) => a + b, 0) / v.length;
}

function statusCor(status) {
  if (status === "Bom") return "#43a047";
  if (status === "Moderado") return "#ffb300";
  if (status === "Ruim") return "#fb8c00";
  if (status === "Péssimo") return "#d32f2f";
  return "#1976d2";
}

// Para limites do tipo "quanto menor, melhor"
function classificarMenorMelhor(value, bomMax, moderadoMax, ruimMax) {
  if (!Number.isFinite(value)) return { status: "Sem dados", conforme: null };
  if (value <= bomMax) return { status: "Bom", conforme: true };
  if (value <= moderadoMax) return { status: "Moderado", conforme: false };
  if (value <= ruimMax) return { status: "Ruim", conforme: false };
  return { status: "Péssimo", conforme: false };
}

// Para faixas (temperatura/umidade) — dentro = Bom
function classificarFaixa(value, min, max) {
  if (!Number.isFinite(value)) return { status: "Sem dados", conforme: null };

  if (value >= min && value <= max) return { status: "Bom", conforme: true };

  // tolerância leve (moderado) — você pode ajustar
  const tol = 1;
  if (value >= min - tol && value <= max + tol) return { status: "Moderado", conforme: false };

  // mais fora
  const tol2 = 3;
  if (value >= min - tol2 && value <= max + tol2) return { status: "Ruim", conforme: false };

  return { status: "Péssimo", conforme: false };
}

function classificarCOIQAr(value) {
  if (!Number.isFinite(value)) return { status: "Sem dados", conforme: null };
  if (value <= 9) return { status: "Bom", conforme: true };
  if (value <= 11) return { status: "Moderado", conforme: false };
  if (value <= 13) return { status: "Ruim", conforme: false };
  return { status: "Péssimo", conforme: false };
}

function classificarH2SNR15(value) {
  if (!Number.isFinite(value)) return { status: "Sem dados", conforme: null };
  if (value <= 5) return { status: "Bom", conforme: true };
  if (value <= 8) return { status: "Moderado", conforme: false };
  return { status: "Péssimo", conforme: false };
}

// =================== LIMITES ABNT NBR 17037:2023 ===================
// - CO2: limite por diferença para o ar exterior: ΔCO2 <= 700 ppm
// - PM10: média 24h <= 50 µg/m³
// - PM2.5: média 24h <= 25 µg/m³
// - Temperatura: 21 a 26 °C
// - Umidade: 35 a 65 % (faixa recomendável)

const NBR = {
  deltaCO2Max: 700,
  pm10_24h: 50,
  pm25_24h: 25,
  tempMin: 21,
  tempMax: 26,
  rhMin: 35,
  rhMax: 65,
};

// =================== PARAMETROS (base) ===================
const parametrosBase = [
  { key: "CO2", label: "CO₂", unidade: "ppm", min: 350, max: 2500, fonte: "ABNT NBR 17037:2023" },

  { key: "PM2_5", label: "MP2.5", unidade: "µg/m³", min: 0, max: 150, fonte: "ABNT NBR 17037:2023 (média 24h)" },
  { key: "PM10", label: "MP10", unidade: "µg/m³", min: 0, max: 200, fonte: "ABNT NBR 17037:2023 (média 24h)" },

  // ===== VOC (MED) ppm =====
  { key: "VOC_ppm", label: "VOC", unidade: "ppm", min: 0, max: 500, fonte: "Leitura indicativa de COV (sem limite legal direto)" },

  // ===== VOC (Habitat) index =====
  { key: "VOC", label: "VOC", unidade: "index", min: 0, max: 500, fonte: "Indicador relativo de VOC (sem limite legal direto)" },

  // ===== PRO gases =====
  { key: "CO", label: "CO", unidade: "ppm", min: 0, max: 50, fonte: "IQAr/MMA jan-2025 / Conama 506:2024" },
  { key: "H2S", label: "H₂S", unidade: "ppm", min: 0, max: 10, fonte: "NR-15 Anexo 11" },

  { key: "NOx", label: "NOx", unidade: "index", min: 0, max: 500, fonte: "Indicador relativo de NOx (sem equivalência direta)" },
  { key: "HCHO", label: "Formaldeído", unidade: "mg/m³", min: 0, max: 2, fonte: "Leitura indicativa de formaldeído" },

  { key: "Umid", label: "Umidade", unidade: "%", min: 0, max: 100, fonte: "ABNT NBR 17037:2023" },
  { key: "Temp", label: "Temperatura", unidade: "°C", min: 0, max: 50, fonte: "ABNT NBR 17037:2023" },

  { key: "pressao", label: "Pressão", unidade: "hPa", min: 800, max: 1200, fonte: "" },
  { key: "PM1", label: "MP1", unidade: "µg/m³", min: 0, max: 150, fonte: "" },
];

const parametroCores = {
  CO2: "#43a047",
  PM2_5: "#ffa726",
  PM10: "#e65100",
  PM1: "#bdbdbd",
  VOC_ppm: "#00c853",
  VOC: "#00c853",
  NOx: "#90caf9",
  HCHO: "#26a69a",
  CO: "#ffd54f",
  H2S: "#ce93d8",
  Temp: "#ff7043",
  Umid: "#1e88e5",
  pressao: "#ab47bc",
};

const PERFIS_EQUIPAMENTO = {
  HABITAT: ["CO2", "PM1", "PM2_5", "PM10", "VOC", "NOx", "Temp", "Umid", "pressao"],
  MED: ["CO2", "PM1", "PM2_5", "PM10", "VOC_ppm", "HCHO", "Temp", "Umid", "pressao"],
  PRO: ["PM1", "PM2_5", "PM10", "CO", "H2S", "VOC", "NOx", "Temp", "Umid"],
};

function camposPermitidosPorModelo(modelo) {
  return new Set(PERFIS_EQUIPAMENTO[modelo] || PERFIS_EQUIPAMENTO.HABITAT);
}

// =================== CLASSIFICAÇÕES (NBR) ===================
function faixaNBR(paramKey, value, ctx = {}) {
  if (value == null || !Number.isFinite(value)) return { cor: "#1976d2", rotulo: "Sem dado" };

  // CO2: usa ΔCO2 se houver externo
  if (paramKey === "CO2") {
    const { co2Out } = ctx;
    if (Number.isFinite(co2Out)) {
      const deltaRaw = value - co2Out;
const excesso = Math.max(0, deltaRaw); // ✅ se interno < externo, excesso = 0 (melhor que externo)

if (excesso <= NBR.deltaCO2Max) return { cor: "#43a047", rotulo: "Conforme (excesso≤700)" };
if (excesso <= NBR.deltaCO2Max + 200) return { cor: "#ffeb3b", rotulo: "Atenção" };
return { cor: "#d32f2f", rotulo: "Não conforme" };
    }
    // Sem externo: não dá para aplicar ΔCO2 corretamente
    return { cor: "#1976d2", rotulo: "Informativo (sem externo)" };
  }

  if (paramKey === "PM2_5") {
    if (value <= NBR.pm25_24h) return { cor: "#43a047", rotulo: "Conforme (≤25)" };
    if (value <= 35) return { cor: "#ffeb3b", rotulo: "Atenção" };
    return { cor: "#d32f2f", rotulo: "Não conforme" };
  }

  if (paramKey === "PM10") {
    if (value <= NBR.pm10_24h) return { cor: "#43a047", rotulo: "Conforme (≤50)" };
    if (value <= 80) return { cor: "#ffeb3b", rotulo: "Atenção" };
    return { cor: "#d32f2f", rotulo: "Não conforme" };
  }

  if (paramKey === "Temp") {
    if (value >= NBR.tempMin && value <= NBR.tempMax) return { cor: "#43a047", rotulo: "Recomendável" };
    if (value >= 20 && value <= 27) return { cor: "#ffeb3b", rotulo: "Atenção" };
    return { cor: "#d32f2f", rotulo: "Fora da faixa" };
  }

  if (paramKey === "Umid") {
    if (value >= NBR.rhMin && value <= NBR.rhMax) return { cor: "#43a047", rotulo: "Recomendável" };
    if ((value >= 30 && value < NBR.rhMin) || (value > NBR.rhMax && value <= 70)) return { cor: "#ffeb3b", rotulo: "Atenção" };
    return { cor: "#d32f2f", rotulo: "Fora da faixa" };
  }

  if (paramKey === "CO") {
    if (value <= 9) return { cor: "#43a047", rotulo: "Boa (8h ≤ 9)" };
    if (value <= 11) return { cor: "#fbc02d", rotulo: "Moderada" };
    if (value <= 13) return { cor: "#fb8c00", rotulo: "Ruim" };
    if (value <= 15) return { cor: "#ef6c00", rotulo: "Muito ruim" };
    return { cor: "#d32f2f", rotulo: "Péssima" };
  }

  if (paramKey === "H2S") {
    if (value <= 5) return { cor: "#43a047", rotulo: "Boa" };
    if (value <= 8) return { cor: "#fbc02d", rotulo: "Atenção" };
    return { cor: "#d32f2f", rotulo: "Acima NR-15" };
  }

  // Outros: mantém informativo
  return { cor: "#1976d2", rotulo: "Informativo" };
}

// Detecta modelo (Habitat / Med / Pro) com base nos sensores disponíveis
function detectarModelo(dados) {
  const hasHCHO = dados?.HCHO != null; // Med
  const hasCO = dados?.CO != null || dados?.co != null;
  const hasH2S = dados?.H2S != null || dados?.h2s != null;

  if (hasHCHO) return "MED";
  if (hasCO || hasH2S) return "PRO";
  return "HABITAT";
}

function modeloTitulo(m) {
  if (m === "MED") return "Sense Med";
  if (m === "PRO") return "Sense Pro";
  return "Sense Habitat";
}

export default function Dashboard({ selectedMac, ambienteNome, selectedMacOut, ambienteNomeOut }) {
  const [dadosIn, setDadosIn] = useState({});
  const [dadosOut, setDadosOut] = useState({});
  const [historicoIn, setHistoricoIn] = useState([]);
  const [historicoOut, setHistoricoOut] = useState([]);
  const [selecionados, setSelecionados] = useState([]);
  const [compararExterno, setCompararExterno] = useState(true);

  const [periodo, setPeriodo] = useState({
    ini: (() => {
      const d = new Date();
      d.setHours(d.getHours() - 24, 0, 0, 0);
      return d;
    })(),
    fim: new Date(),
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  // Reset ao trocar MACs
  useEffect(() => {
    setDadosIn({});
    setHistoricoIn([]);
    setSelecionados([]);
  }, [selectedMac]);

  useEffect(() => {
    setDadosOut({});
    setHistoricoOut([]);
  }, [selectedMacOut]);

  // ================== DADOS ATUAIS (INTERNO) ==================
  useEffect(() => {
    let interval;

    const fetchData = async () => {
      if (!selectedMac) return setDadosIn({});
      try {
        const res = await api.get("/data");
        const all = res.data?.[selectedMac] || {};

        // Padronizar PM2_5
        if (all["PM2.5"] != null && all["PM2_5"] == null) all["PM2_5"] = all["PM2.5"];
        // Padronizar pressão
        if (all["pressao"] == null && all["Pressao"] != null) all["pressao"] = all["Pressao"];

        // MED: VOC é ppm (o campo pode vir como VOC)
        const isMed = all["HCHO"] != null;
        if (isMed && all["VOC_ppm"] == null && all["VOC"] != null) all["VOC_ppm"] = all["VOC"];

        // PRO: padroniza CO/H2S
        if (all["CO"] == null && all["co"] != null) all["CO"] = all["co"];
        if (all["H2S"] == null && all["h2s"] != null) all["H2S"] = all["h2s"];

        const now = new Date().toLocaleTimeString("pt-BR", { hour12: false });
        setDadosIn({ ...all, _lastUpdate: now });
      } catch {
        setDadosIn((prev) => ({ ...prev, _lastUpdate: prev?._lastUpdate }));
      }
    };

    fetchData();
    interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [selectedMac]);

  // ================== DADOS ATUAIS (EXTERNO) ==================
  useEffect(() => {
    let interval;

    const fetchDataOut = async () => {
      if (!selectedMacOut) return setDadosOut({});
      try {
        const res = await api.get("/data");
        const all = res.data?.[selectedMacOut] || {};

        if (all["PM2.5"] != null && all["PM2_5"] == null) all["PM2_5"] = all["PM2.5"];
        if (all["pressao"] == null && all["Pressao"] != null) all["pressao"] = all["Pressao"];

        const isMed = all["HCHO"] != null;
        if (isMed && all["VOC_ppm"] == null && all["VOC"] != null) all["VOC_ppm"] = all["VOC"];

        if (all["CO"] == null && all["co"] != null) all["CO"] = all["co"];
        if (all["H2S"] == null && all["h2s"] != null) all["H2S"] = all["h2s"];

        setDadosOut(all);
      } catch {
        setDadosOut((prev) => ({ ...prev }));
      }
    };

    if (!selectedMacOut) return;
    fetchDataOut();
    interval = setInterval(fetchDataOut, 30000);
    return () => clearInterval(interval);
  }, [selectedMacOut]);

  // ================== HISTÓRICO (INTERNO) ==================
  useEffect(() => {
    const fetchHistoric = async () => {
      if (!selectedMac) return setHistoricoIn([]);
      try {
        const res = await api.get("/historic-data", {
          params: {
            mac: selectedMac,
            start_date: periodo.ini.toISOString().slice(0, 19).replace("T", " "),
            end_date: periodo.fim.toISOString().slice(0, 19).replace("T", " "),
          },
        });
        setHistoricoIn(res.data || []);
      } catch {
        setHistoricoIn([]);
      }
    };
    fetchHistoric();
  }, [selectedMac, periodo]);

  // ================== HISTÓRICO (EXTERNO) ==================
  useEffect(() => {
    const fetchHistoricOut = async () => {
      if (!selectedMacOut) return setHistoricoOut([]);
      try {
        const res = await api.get("/historic-data", {
          params: {
            mac: selectedMacOut,
            start_date: periodo.ini.toISOString().slice(0, 19).replace("T", " "),
            end_date: periodo.fim.toISOString().slice(0, 19).replace("T", " "),
          },
        });
        setHistoricoOut(res.data || []);
      } catch {
        setHistoricoOut([]);
      }
    };
    fetchHistoricOut();
  }, [selectedMacOut, periodo]);

  // ================== FILTRAR PARÂMETROS POR MODELO ==================
  const modeloIn = useMemo(() => detectarModelo(dadosIn), [dadosIn]);
  const modeloOut = useMemo(() => detectarModelo(dadosOut), [dadosOut]);

  const parametros = useMemo(() => {
    const permitidos = camposPermitidosPorModelo(modeloIn);

    return parametrosBase.filter((p) => {
      if (!permitidos.has(p.key)) return false;
      if (p.key === "VOC_ppm" && modeloIn !== "MED") return false;
      if (p.key === "VOC" && modeloIn === "MED") return false;
      if (p.key === "HCHO" && modeloIn !== "MED") return false;
      return true;
    });
  }, [modeloIn]);

  // ================== HISTÓRICO POR PARÂMETRO (INTERNO) ==================
  const historicoPorParametroIn = useMemo(() => {
    const out = {};
    const isMed = modeloIn === "MED";

    parametrosBase.forEach((param) => {
      out[param.key] = (historicoIn || [])
        .filter((d) => {
          const sensor = (d.sensor || "").replace(".", "_").toUpperCase();

          if (param.key === "PM2_5") return sensor === "PM2_5";

          if (param.key === "VOC_ppm") return sensor === "VOC_PPM" || (isMed && sensor === "VOC");
          if (param.key === "VOC") return sensor === "VOC" && !isMed;

          if (param.key === "CO") return sensor === "CO" || sensor === "CO_PPM";
          if (param.key === "H2S") return sensor === "H2S" || sensor === "H2S_PPM";

          return sensor === param.key.toUpperCase();
        })
        .map((d) => ({
          x: ajustarHoraBrasilia(d.timestamp).toLocaleString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          }),
          y: parseFloat(d.value),
        }))
        .filter((p) => Number.isFinite(p.y));
    });

    return out;
  }, [historicoIn, modeloIn]);

  // ================== HISTÓRICO POR PARÂMETRO (EXTERNO) ==================
  const historicoPorParametroOut = useMemo(() => {
    const out = {};
    const isMed = modeloOut === "MED";

    parametrosBase.forEach((param) => {
      out[param.key] = (historicoOut || [])
        .filter((d) => {
          const sensor = (d.sensor || "").replace(".", "_").toUpperCase();

          if (param.key === "PM2_5") return sensor === "PM2_5";

          if (param.key === "VOC_ppm") return sensor === "VOC_PPM" || (isMed && sensor === "VOC");
          if (param.key === "VOC") return sensor === "VOC" && !isMed;

          if (param.key === "CO") return sensor === "CO" || sensor === "CO_PPM";
          if (param.key === "H2S") return sensor === "H2S" || sensor === "H2S_PPM";

          return sensor === param.key.toUpperCase();
        })
        .map((d) => ({
          x: ajustarHoraBrasilia(d.timestamp).toLocaleString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          }),
          y: parseFloat(d.value),
        }))
        .filter((p) => Number.isFinite(p.y));
    });

    return out;
  }, [historicoOut, modeloOut]);

  // ================== PARÂMETROS ATIVOS (apenas do modelo interno) ==================
  const parametrosAtivos = useMemo(() => {
    return parametros.filter((p) => {
      const v = dadosIn?.[p.key];
      const temAgora = v !== null && v !== undefined;
      const temHistorico = (historicoPorParametroIn[p.key] || []).length > 0;
      return temAgora || temHistorico;
    });
  }, [parametros, dadosIn, historicoPorParametroIn]);

  const ativosSet = useMemo(() => new Set(parametrosAtivos.map((p) => p.key)), [parametrosAtivos]);

  useEffect(() => {
    if (!selectedMac) {
      setSelecionados([]);
      return;
    }
    const ativosKeys = parametrosAtivos.map((p) => p.key);
    setSelecionados((prev) => {
      if (!prev || prev.length === 0) return ativosKeys;
      const filtrado = prev.filter((k) => ativosSet.has(k));
      return filtrado.length ? filtrado : ativosKeys;
    });
  }, [selectedMac, parametrosAtivos, ativosSet]);

  function toggleParametro(key) {
    setSelecionados((sel) => (sel.includes(key) ? sel.filter((p) => p !== key) : [...sel, key]));
  }

  // ================== DATASETS (INTERNO + opcional EXTERNO) ==================
  const datasets = useMemo(() => {
    const ds = [];

    // Interno
    parametrosAtivos
      .filter((p) => selecionados.includes(p.key))
      .forEach((p) => {
        ds.push({
          label: `${p.label} (Int)`,
          data: (historicoPorParametroIn[p.key] || []).map((point) => point.y),
          borderColor: parametroCores[p.key] || "#888",
          backgroundColor: (parametroCores[p.key] || "#888") + "33",
          tension: 0.3,
          yAxisID: ["VOC", "NOx"].includes(p.key) ? "y1" : "y",
          pointRadius: 1.5,
          fill: false,
        });
      });

    // Externo (somente parâmetros comparáveis)
    if (compararExterno && selectedMacOut) {
      const comparaveis = new Set(["CO2", "PM2_5", "PM10", "Temp", "Umid", "pressao", "PM1"]);
      parametrosAtivos
        .filter((p) => selecionados.includes(p.key) && comparaveis.has(p.key))
        .forEach((p) => {
          ds.push({
            label: `${p.label} (Ext)`,
            data: (historicoPorParametroOut[p.key] || []).map((point) => point.y),
            borderColor: (parametroCores[p.key] || "#888") + "AA",
            backgroundColor: (parametroCores[p.key] || "#888") + "11",
            tension: 0.3,
            yAxisID: ["VOC", "NOx"].includes(p.key) ? "y1" : "y",
            pointRadius: 1.2,
            borderDash: [6, 6],
            fill: false,
          });
        });
    }

    return ds;
  }, [
    parametrosAtivos,
    selecionados,
    historicoPorParametroIn,
    historicoPorParametroOut,
    compararExterno,
    selectedMacOut,
  ]);

  const labels = useMemo(() => {
    const mainParam = selecionados.find((k) => (historicoPorParametroIn[k] || []).length);
    return mainParam ? historicoPorParametroIn[mainParam].map((p) => p.x) : [];
  }, [selecionados, historicoPorParametroIn]);


 // ================== MÉDIAS + STATUS INDIVIDUAL (NBR) NO PERÍODO ==================
const mediasNBRDetalhado = useMemo(() => {
  const avgIn = {
    PM2_5: mean((historicoPorParametroIn.PM2_5 || []).map((p) => p.y)),
    PM10: mean((historicoPorParametroIn.PM10 || []).map((p) => p.y)),
    Temp: mean((historicoPorParametroIn.Temp || []).map((p) => p.y)),
    Umid: mean((historicoPorParametroIn.Umid || []).map((p) => p.y)),
    CO2: mean((historicoPorParametroIn.CO2 || []).map((p) => p.y)),
    CO: mean((historicoPorParametroIn.CO || []).map((p) => p.y)),
    H2S: mean((historicoPorParametroIn.H2S || []).map((p) => p.y)),
  };

  const avgOut = selectedMacOut
    ? { CO2: mean((historicoPorParametroOut.CO2 || []).map((p) => p.y)) }
    : {};

  const deltaRaw =
    Number.isFinite(avgIn.CO2) && Number.isFinite(avgOut.CO2)
      ? avgIn.CO2 - avgOut.CO2
      : undefined;

  const excessoCO2 = Number.isFinite(deltaRaw) ? Math.max(0, deltaRaw) : undefined;

  const itens = [
    {
      key: "PM2_5",
      label: "PM2.5",
      valor: avgIn.PM2_5,
      unidade: "µg/m³",
      criterio: `≤ ${NBR.pm25_24h} (média 24h)`,
      ...classificarMenorMelhor(avgIn.PM2_5, NBR.pm25_24h, 30, 35),
    },
    {
      key: "PM10",
      label: "PM10",
      valor: avgIn.PM10,
      unidade: "µg/m³",
      criterio: `≤ ${NBR.pm10_24h} (média 24h)`,
      ...classificarMenorMelhor(avgIn.PM10, NBR.pm10_24h, 65, 80),
    },
    {
      key: "Temp",
      label: "Temperatura",
      valor: avgIn.Temp,
      unidade: "°C",
      criterio: `${NBR.tempMin}–${NBR.tempMax} °C`,
      ...classificarFaixa(avgIn.Temp, NBR.tempMin, NBR.tempMax),
    },
    {
      key: "Umid",
      label: "Umidade",
      valor: avgIn.Umid,
      unidade: "%",
      criterio: `${NBR.rhMin}–${NBR.rhMax} %`,
      ...classificarFaixa(avgIn.Umid, NBR.rhMin, NBR.rhMax),
    },
  ];

  if (modeloIn !== "PRO") {
    const co2 = (() => {
      if (!selectedMacOut || !Number.isFinite(excessoCO2)) {
        return { status: "Sem externo", conforme: null };
      }
      return classificarMenorMelhor(excessoCO2, NBR.deltaCO2Max, NBR.deltaCO2Max + 200, NBR.deltaCO2Max + 500);
    })();

    itens.push({
      key: "CO2",
      label: "ΔCO₂ (Int−Ext)",
      valor: Number.isFinite(deltaRaw) ? deltaRaw : undefined,
      unidade: "ppm",
      criterio: `excesso ≤ ${NBR.deltaCO2Max} ppm`,
      ...co2,
      extra: {
        avgInCO2: avgIn.CO2,
        avgOutCO2: avgOut.CO2,
        excessoCO2,
      },
    });
  }

  if (modeloIn === "PRO") {
    itens.push(
      {
        key: "CO",
        label: "CO",
        valor: avgIn.CO,
        unidade: "ppm",
        criterio: "IQAr CO (8h): boa ≤9; mod. ≤11; ruim ≤13; m.ruim ≤15",
        ...classificarCOIQAr(avgIn.CO),
      },
      {
        key: "H2S",
        label: "H₂S",
        valor: avgIn.H2S,
        unidade: "ppm",
        criterio: "NR-15: LT 8 ppm",
        ...classificarH2SNR15(avgIn.H2S),
      }
    );
  }

  const itensAvaliaveis = itens.filter((i) => i.conforme !== null);
  const temDados = itensAvaliaveis.length > 0;
  const conformeGeral = temDados ? itensAvaliaveis.every((i) => i.conforme) : false;

  return {
    avgIn,
    avgOut,
    deltaRaw,
    excessoCO2,
    itens,
    temDados,
    conformeGeral,
  };
}, [historicoPorParametroIn, historicoPorParametroOut, modeloIn, selectedMacOut]);

 
  // ================== EXPORTAR PDF ==================
function sanitizePdfText(s) {
  return String(s ?? "")
    // sinais comuns
    .replaceAll("≤", "<=")
    .replaceAll("≥", ">=")
    .replaceAll("Δ", "Delta ")
    .replaceAll("µ", "u")

    // subscritos (CO₂ etc.)
    .replaceAll("₀", "0")
    .replaceAll("₁", "1")
    .replaceAll("₂", "2")
    .replaceAll("₃", "3")
    .replaceAll("₄", "4")
    .replaceAll("₅", "5")
    .replaceAll("₆", "6")
    .replaceAll("₇", "7")
    .replaceAll("₈", "8")
    .replaceAll("₉", "9")

    // travessões e hífens “bonitos”
    .replaceAll("–", "-")
    .replaceAll("—", "-")

    // aspas “inteligentes”
    .replaceAll("“", '"')
    .replaceAll("”", '"')
    .replaceAll("‘", "'")
    .replaceAll("’", "'")

    // NBSP
    .replace(/\u00A0/g, " ")

    // remove caracteres de controle que às vezes aparecem
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "");
}
function exportarPDF() {
  import("html2canvas").then((html2canvas) =>
    import("jspdf").then((jsPDF) => {
      const input = document.getElementById("grafico-historico");
      if (!input) return;

      html2canvas.default(input).then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF.default({
          orientation: "landscape",
          unit: "pt",
          format: "a4",
        });

        // --- Cabeçalho ---
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(16);
        pdf.text(sanitizePdfText("Relatório de Qualidade do Ar"), 40, 40);

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(11);
        pdf.text(sanitizePdfText(`Interno: ${ambienteNome || selectedMac || ""}`), 40, 60);

        if (selectedMacOut) {
          pdf.text(sanitizePdfText(`Externo: ${ambienteNomeOut || selectedMacOut || ""}`), 40, 78);
        }

        pdf.text(
          sanitizePdfText(`Período: ${periodo.ini.toLocaleString()} a ${periodo.fim.toLocaleString()}`),
          40,
          96
        );

        // --- Imagem do gráfico ---
        pdf.addImage(imgData, "PNG", 40, 120, 750, 250);

        // --- Conformidade geral ---
        const geralTxt = mediasNBRDetalhado?.temDados
          ? mediasNBRDetalhado.conformeGeral
            ? "CONFORME"
            : "NAO CONFORME"
          : "SEM DADOS";

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(12);
        pdf.text(sanitizePdfText(`Conformidade geral: ${geralTxt}`), 40, 390);

        // --- Status por parâmetro (médias do período) ---
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(12);
        pdf.text(sanitizePdfText("Status por parametro (media do periodo):"), 40, 412);

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(10);

        const maxWidth = 760; // largura útil aproximada no A4 landscape com margens
        let y = 430;

        const itens = mediasNBRDetalhado?.itens || [];
        if (!itens.length) {
          pdf.text(sanitizePdfText("Sem dados suficientes para listar parametros."), 40, y);
        } else {
          for (const it of itens) {
  // força um label “limpo” pro CO2 (evita qualquer resto de CO₂/Δ)
  const labelRaw =
    it?.key === "CO2"
      ? "Delta CO2 (Int-Ext)"
      : (it?.label || it?.key || "Parametro");

  const label = sanitizePdfText(labelRaw);

  const hasValue = Number.isFinite(it?.valor);
  const valorTxt = hasValue
    ? sanitizePdfText(`${formatValue(it.valor, it.key === "CO2" ? 0 : 2)} ${it.unidade || ""}`.trim())
    : "--";

  const statusTxt =
    it?.conforme === null
      ? sanitizePdfText(it?.status || "Sem avaliacao")
      : it.conforme
      ? "Conforme"
      : "Nao conforme";

  const criterioTxt = it?.criterio
    ? ` (criterio: ${sanitizePdfText(it.criterio)})`
    : "";

  const line = `• ${label}: ${valorTxt} — ${statusTxt}${criterioTxt}`;

  const lines = pdf.splitTextToSize(line, maxWidth);

            // se estourar a página, cria nova
            const bottomLimit = 560; // aprox. fim da página em landscape
            if (y + lines.length * 14 > bottomLimit) {
              pdf.addPage("a4", "landscape");
              y = 60;

              pdf.setFont("helvetica", "bold");
              pdf.setFontSize(12);
              pdf.text(sanitizePdfText("Status por parametro (continua):"), 40, 40);

              pdf.setFont("helvetica", "normal");
              pdf.setFontSize(10);
            }

            pdf.text(lines, 40, y);
            y += lines.length * 14;

            // detalhes extras do CO2 (se quiser manter)
            if (it.key === "CO2" && it.extra) {
              const a = it.extra;

              const det1 = sanitizePdfText(
                `   - CO2 medio interno: ${Number.isFinite(a.avgInCO2) ? `${formatValue(a.avgInCO2, 0)} ppm` : "--"}`
              );
              const det2 = sanitizePdfText(
                `   - CO2 medio externo: ${Number.isFinite(a.avgOutCO2) ? `${formatValue(a.avgOutCO2, 0)} ppm` : "--"}`
              );
              const det3 = sanitizePdfText(
                `   - Excesso considerado (max(0, Int-Ext)): ${
                  Number.isFinite(a.excessoCO2) ? `${formatValue(a.excessoCO2, 0)} ppm` : "--"
                }`
              );

              const detLines = [det1, det2, det3];

              if (y + detLines.length * 14 > bottomLimit) {
                pdf.addPage("a4", "landscape");
                y = 60;
              }

              pdf.text(detLines, 40, y);
              y += detLines.length * 14;
            }
          }
        }

        // --- Referências e observações do relatório ---
        const refLines = [
          sanitizePdfText("Referências do relatório:"),
          sanitizePdfText("- ABNT NBR 17037:2023: CO2, MP2.5, MP10, temperatura e umidade."),
          sanitizePdfText("- IQAr/MMA (jan/2025) / Conama 506:2024: CO (faixas de 8h)."),
          sanitizePdfText("- NR-15 Anexo 11: H2S (LT 8 ppm para jornada de até 48 h/semana)."),
          sanitizePdfText("- VOC/NOx em índice, VOC em ppm e formaldeído são exibidos como indicadores técnicos/triagem, sem equivalência legal direta universal no formato do sensor.")
        ];

        const addWrappedLine = (line, fontStyle = "normal") => {
          const lines = pdf.splitTextToSize(line, maxWidth);
          const bottomLimit = 560;
          if (y + lines.length * 14 > bottomLimit) {
            pdf.addPage("a4", "landscape");
            y = 60;
          }
          pdf.setFont("helvetica", fontStyle);
          pdf.setFontSize(fontStyle === "bold" ? 11 : 10);
          pdf.text(lines, 40, y);
          y += lines.length * 14;
        };

        y += 10;
        addWrappedLine(refLines[0], "bold");
        for (const line of refLines.slice(1)) addWrappedLine(line, "normal");

        // --- Salvar ---
        pdf.save(
          `Relatorio_AirQuality_${(ambienteNome || selectedMac || "ambiente")}_${new Date()
            .toISOString()
            .slice(0, 10)}.pdf`
        );
      });
    })
  );
}

  // ================== CONTEXTO p/ FAIXA (CO2 externo) ==================
  const ctxFaixa = useMemo(() => ({ co2Out: dadosOut?.CO2 }), [dadosOut]);

  // ================== TÍTULO PAINEL ==================
  const tituloPainel = useMemo(() => {
    if (!selectedMac) return "Painel de Qualidade do Ar";
    return `Painel de Qualidade do Ar — ${modeloTitulo(modeloIn)}`;
  }, [selectedMac, modeloIn]);

  // ================== UI ==================
  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        overflowX: "auto",
        px: { xs: 0.5, sm: 2, md: 3 },
        py: { xs: 1, sm: 2 },
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          mb: 2,
          gap: 1.5,
        }}
      >
        {ambienteNome && (
          <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ color: "primary.main" }}>
            Interno: {ambienteNome}
          </Typography>
        )}

        {selectedMacOut && (
          <Typography variant={isMobile ? "subtitle2" : "subtitle1"} sx={{ color: "info.main" }}>
            Externo: {ambienteNomeOut || selectedMacOut}
          </Typography>
        )}

        {dadosIn && dadosIn._lastUpdate && (
          <Chip
            label={`Última atualização: ${dadosIn._lastUpdate}`}
            size="small"
            sx={{ ml: { sm: 1, xs: 0 }, mt: { xs: 1, sm: 0 } }}
            color="info"
            variant="outlined"
          />
        )}
      </Box>

      <Typography variant={isMobile ? "h6" : "h4"} gutterBottom sx={{ mb: isMobile ? 1 : 2 }}>
  {!selectedMac ? "Painel de Qualidade do Ar" : `Painel de Qualidade do Ar — ${modeloTitulo(modeloIn)}`}
</Typography>

      {!selectedMac && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Vá em <b>Ambientes</b> e selecione um equipamento <b>INTERNO</b> para visualizar o painel.
        </Alert>
      )}

      {!!selectedMacOut && (
        <FormControlLabel
          sx={{ mb: 1 }}
          control={<Checkbox checked={compararExterno} onChange={(e) => setCompararExterno(e.target.checked)} />}
          label="Comparar com Externo no gráfico (quando houver dados)"
        />
      )}

      {/* ================== GAUGES: INTERNO ================== */}
      <Typography variant="subtitle1" sx={{ fontWeight: 800, mt: 1, mb: 1 }}>
        Interno
      </Typography>

      <Grid
        container
        spacing={isMobile ? 1 : 3}
        mb={2}
        wrap="wrap"
        sx={{
          width: "100%",
          flexWrap: "wrap",
          overflowX: "hidden",
          justifyContent: { xs: "center", sm: "flex-start" },
        }}
      >
        {parametrosAtivos.map((param) => {
          const raw = dadosIn?.[param.key];
          const valor = raw != null ? parseFloat(raw) : null;

          const faixa = faixaNBR(param.key, valor, ctxFaixa);

          const limiteTexto =
            param.key === "CO2"
              ? Number.isFinite(dadosOut?.CO2)
                ? `ΔCO₂ ≤ ${NBR.deltaCO2Max} ppm`
                : "Requer externo p/ ΔCO₂"
              : param.key === "PM2_5"
              ? `≤ ${NBR.pm25_24h} (média 24h)`
              : param.key === "PM10"
              ? `≤ ${NBR.pm10_24h} (média 24h)`
              : param.key === "Temp"
              ? `${NBR.tempMin}–${NBR.tempMax} °C`
              : param.key === "Umid"
              ? `${NBR.rhMin}–${NBR.rhMax} %`
              : param.key === "CO"
              ? "IQAr CO 8h: boa ≤9; mod. ≤11; ruim ≤13; m.ruim ≤15"
              : param.key === "H2S"
              ? "NR-15: LT 8 ppm"
              : param.key === "VOC_ppm"
              ? "Leitura indicativa de COV"
              : param.key === "VOC"
              ? "Índice relativo de VOC"
              : param.key === "NOx"
              ? "Índice relativo de NOx"
              : param.key === "HCHO"
              ? "Uso indicativo para formaldeído"
              : "-";

          return (
            <Grid
              key={`in-${param.key}`}
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
              sx={{
                display: "flex",
                justifyContent: "center",
                minWidth: { xs: "92vw", sm: 200, md: 210, lg: 220 },
                maxWidth: { xs: "95vw", sm: 210, md: 230, lg: 250 },
              }}
            >
              <Box
                sx={{
                  bgcolor: (t) => t.palette.background.paper,
                  p: { xs: 1.3, sm: 2 },
                  borderRadius: 3,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  boxShadow: 3,
                  minHeight: { xs: 160, sm: 210, md: 230 },
                  mb: isMobile ? 1 : 0,
                }}
              >
                <Typography variant="subtitle2" color="text.primary" mb={0.7} sx={{ fontSize: { xs: 15, sm: 17 } }}>
                  {param.label}
                </Typography>

                <Box sx={{ width: isMobile ? 75 : 110, height: isMobile ? 75 : 110 }}>
                  <CircularProgressbarWithChildren
                    value={
                      valor == null
                        ? 0
                        : Math.max(
                            0,
                            Math.min(100, (100 * (valor - (param.min ?? 0))) / ((param.max ?? 1) - (param.min ?? 0)))
                          )
                    }
                    strokeWidth={12}
                    styles={buildStyles({
                      pathColor: faixa.cor,
                      trailColor: "#444",
                      textColor: "#fff",
                      backgroundColor: "#222",
                    })}
                  >
                    <Typography variant="h6" sx={{ color: faixa.cor, fontWeight: 700, fontSize: isMobile ? 16 : 22 }}>
                      {valor != null ? formatValue(valor) : "--"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? 11 : 13 }}>
                      {param.unidade}
                    </Typography>
                  </CircularProgressbarWithChildren>
                </Box>

                <Chip
                  label={faixa.rotulo}
                  size="small"
                  sx={{ mt: 1, mb: 0.5, bgcolor: faixa.cor, color: "#fff", fontWeight: 700 }}
                />

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, fontSize: isMobile ? 10.5 : 12, textAlign: "center" }}
                >
                  Limite: {limiteTexto}
                </Typography>

                {param.fonte && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0, fontSize: isMobile ? 9.5 : 11, textAlign: "center" }}
                  >
                    {param.fonte}
                  </Typography>
                )}

                {valor != null && faixa.cor === "#d32f2f" && (
                  <Alert severity="error" sx={{ mt: 1, mb: 0, p: 0.5, width: "100%", fontSize: 13 }}>
                    Fora do recomendado!
                  </Alert>
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {/* ================== GAUGES: EXTERNO (opcional) ================== */}
      {selectedMacOut && (
        <>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, mt: 2, mb: 1 }}>
            Externo (referência)
          </Typography>

          <Grid
            container
            spacing={isMobile ? 1 : 3}
            mb={2}
            wrap="wrap"
            sx={{
              width: "100%",
              flexWrap: "wrap",
              overflowX: "hidden",
              justifyContent: { xs: "center", sm: "flex-start" },
            }}
          >
            {parametrosAtivos
              .filter((p) => ["CO2", "PM2_5", "PM10", "Temp", "Umid", "pressao", "PM1"].includes(p.key))
              .map((param) => {
                const raw = dadosOut?.[param.key];
                const valor = raw != null ? parseFloat(raw) : null;
                const faixa = { cor: "#1976d2", rotulo: "Referência" };

                return (
                  <Grid
                    key={`out-${param.key}`}
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    lg={3}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      minWidth: { xs: "92vw", sm: 200, md: 210, lg: 220 },
                      maxWidth: { xs: "95vw", sm: 210, md: 230, lg: 250 },
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: (t) => t.palette.background.paper,
                        p: { xs: 1.3, sm: 2 },
                        borderRadius: 3,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        boxShadow: 2,
                        minHeight: { xs: 150, sm: 200, md: 220 },
                        mb: isMobile ? 1 : 0,
                        opacity: 0.95,
                      }}
                    >
                      <Typography variant="subtitle2" color="text.primary" mb={0.7} sx={{ fontSize: { xs: 15, sm: 17 } }}>
                        {param.label}
                      </Typography>

                      <Box sx={{ width: isMobile ? 75 : 110, height: isMobile ? 75 : 110 }}>
                        <CircularProgressbarWithChildren
                          value={
                            valor == null
                              ? 0
                              : Math.max(
                                  0,
                                  Math.min(100, (100 * (valor - (param.min ?? 0))) / ((param.max ?? 1) - (param.min ?? 0)))
                                )
                          }
                          strokeWidth={12}
                          styles={buildStyles({
                            pathColor: faixa.cor,
                            trailColor: "#444",
                            textColor: "#fff",
                            backgroundColor: "#222",
                          })}
                        >
                          <Typography variant="h6" sx={{ color: faixa.cor, fontWeight: 700, fontSize: isMobile ? 16 : 22 }}>
                            {valor != null ? formatValue(valor) : "--"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? 11 : 13 }}>
                            {param.unidade}
                          </Typography>
                        </CircularProgressbarWithChildren>
                      </Box>

                      <Chip label={faixa.rotulo} size="small" sx={{ mt: 1, mb: 0.5, bgcolor: faixa.cor, color: "#fff", fontWeight: 700 }} />
                    </Box>
                  </Grid>
                );
              })}
          </Grid>

          {/* ΔCO2 (instantâneo) */}
          {Number.isFinite(dadosIn?.CO2) && Number.isFinite(dadosOut?.CO2) && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                ΔCO₂ (Interno − Externo)
              </Typography>

              {(() => {
                const deltaRaw = dadosIn.CO2 - dadosOut.CO2;
                const excesso = Math.max(0, deltaRaw);

                const fx =
                  excesso <= NBR.deltaCO2Max
                    ? { cor: "#43a047", rotulo: "Conforme (≤700)" }
                    : { cor: "#d32f2f", rotulo: "Não conforme" };

                const labelValor =
                  deltaRaw >= 0
                    ? `+${formatValue(deltaRaw, 0)} ppm (excesso)`
                    : `${formatValue(Math.abs(deltaRaw), 0)} ppm abaixo do externo`;

                return (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    <Chip label={labelValor} sx={{ bgcolor: fx.cor, color: "#fff", fontWeight: 800 }} />
                    <Typography variant="body2" color="text.secondary">
                      Limite recomendado (NBR 17037): excesso de CO₂ ≤ {NBR.deltaCO2Max} ppm acima do externo.
                    </Typography>
                    <Chip label={fx.rotulo} sx={{ bgcolor: fx.cor, color: "#fff", fontWeight: 700 }} />
                    {deltaRaw < 0 && (
                      <Chip
                        label="Interno melhor que externo"
                        variant="outlined"
                        sx={{ borderColor: "#43a047", color: "#43a047", fontWeight: 700 }}
                      />
                    )}
                  </Box>
                );
              })()}
            </Paper>
          )}
        </>
      )}

      {/* ================== CONTROLES GRÁFICO ================== */}
      <Paper sx={{ mt: 2, p: { xs: 1.5, sm: 2 }, mb: 2, width: "100%", boxSizing: "border-box" }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <Typography variant="subtitle2" sx={{ fontSize: { xs: 13, sm: 15 } }}>
              Período do Gráfico
            </Typography>
            <Box display="flex" gap={1}>
              <DateTimePicker
                label="Início"
                value={periodo.ini}
                onChange={(v) => setPeriodo((p) => ({ ...p, ini: v }))}
                sx={{ minWidth: 110, mr: 1 }}
                ampm={false}
                slotProps={{ textField: { size: isMobile ? "small" : "medium" } }}
              />
              <DateTimePicker
                label="Fim"
                value={periodo.fim}
                onChange={(v) => setPeriodo((p) => ({ ...p, fim: v }))}
                sx={{ minWidth: 110 }}
                ampm={false}
                slotProps={{ textField: { size: isMobile ? "small" : "medium" } }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Typography variant="subtitle2" sx={{ fontSize: { xs: 13, sm: 15 } }}>
              Parâmetros exibidos
            </Typography>
            <FormGroup row>
              {parametrosAtivos.map((param) => (
                <FormControlLabel
                  key={param.key}
                  control={
                    <Checkbox
                      checked={selecionados.includes(param.key)}
                      onChange={() => toggleParametro(param.key)}
                      size={isMobile ? "small" : "medium"}
                    />
                  }
                  label={param.label}
                  sx={{ mr: 1, fontSize: { xs: 11, sm: 14 } }}
                />
              ))}
            </FormGroup>
          </Grid>

          <Grid item xs={12} sm={2} md={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={exportarPDF}
              sx={{ mt: { xs: 1, sm: 0 }, width: "100%" }}
              size={isMobile ? "small" : "medium"}
            >
              Exportar PDF
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* ================== GRÁFICO ================== */}
      <Paper sx={{ mt: 2, p: { xs: 1.5, sm: 3 }, width: "100%", boxSizing: "border-box", overflowX: "auto", display: "block" }}>
        <div
          id="grafico-historico"
          style={{
            minWidth: 350,
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            height: isMobile ? 320 : isTablet ? 260 : 300,
            position: "relative",
          }}
        >
          <Typography variant="h6" mb={1} sx={{ fontSize: isMobile ? 16 : 20 }}>
            Histórico (Int{selectedMacOut && compararExterno ? " + Ext" : ""})
          </Typography>

          <div style={{ position: "absolute", top: 30, left: 0, width: "100%", height: isMobile ? 280 : isTablet ? 220 : 260 }}>
            <Line
              data={{ labels, datasets }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: true, position: "bottom" }, tooltip: { mode: "index", intersect: false } },
                interaction: { mode: "index", intersect: false },
                scales: {
                  x: { title: { display: true, text: "Data/Hora" }, ticks: { maxTicksLimit: 14 } },
                  y: { type: "linear", display: true, position: "left", title: { display: true, text: "ppm / µg/m³" } },
                  y1: { type: "linear", display: true, position: "right", grid: { drawOnChartArea: false }, title: { display: true, text: "VOC/NOx (index)" }, min: 0, max: 500 },
                },
              }}
            />
          </div>
        </div>
      </Paper>

    
      {/* ================== MÉDIAS + STATUS POR PARÂMETRO (NBR) ================== */}
<Paper sx={{ mt: 2, p: { xs: 2, sm: 3 } }}>
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 1 }}>
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 900 }}>
        Status por parâmetro (média do período)
      </Typography>
      <Typography variant="caption" color="text.secondary">
        ABNT NBR 17037 para IAQ e conforto, IQAr/MMA-Conama para CO e NR-15 para H₂S.
      </Typography>
    </Box>

    <Chip
      label={mediasNBRDetalhado.conformeGeral ? "GERAL: CONFORME" : "GERAL: NÃO CONFORME"}
      sx={{
        bgcolor: mediasNBRDetalhado.conformeGeral ? "#43a047" : "#d32f2f",
        color: "#fff",
        fontWeight: 900,
      }}
    />
  </Box>

  {!mediasNBRDetalhado.temDados ? (
    <Alert severity="info">Sem dados suficientes no período para avaliar médias.</Alert>
  ) : (
    <>
      <Grid container spacing={2}>
        {mediasNBRDetalhado.itens.map((it) => {
          const cor = statusCor(it.status);
          const hasValue = Number.isFinite(it.valor);

          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={`nbr-${it.key}`}>
              <Paper sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                  <Typography sx={{ fontWeight: 900 }}>{it.label}</Typography>
                  <Chip
                    label={it.conforme === null ? it.status : it.conforme ? "Conforme" : "Não conforme"}
                    sx={{ bgcolor: cor, color: "#fff", fontWeight: 900 }}
                    size="small"
                  />
                </Box>

                <Typography sx={{ mt: 1, fontSize: 24, fontWeight: 900, color: cor }}>
                  {hasValue ? `${formatValue(it.valor, it.key === "CO2" ? 0 : 2)} ${it.unidade}` : "--"}
                </Typography>

                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                  Critério: {it.criterio}
                </Typography>

                {/* Detalhe extra p/ CO2 */}
                {it.key === "CO2" && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      CO₂ médio interno: {Number.isFinite(it.extra?.avgInCO2) ? `${formatValue(it.extra.avgInCO2, 0)} ppm` : "--"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      CO₂ médio externo: {Number.isFinite(it.extra?.avgOutCO2) ? `${formatValue(it.extra.avgOutCO2, 0)} ppm` : "--"}
                    </Typography>

                    {!selectedMacOut && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        Para avaliar CO₂ pela NBR (ΔCO₂), selecione um equipamento <b>EXTERNO</b>.
                      </Alert>
                    )}
                  </Box>
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
        Nota: PM2.5/PM10 usam <b>média em 24h</b>; CO usa referência de <b>8h</b>; H₂S usa o limite de tolerância da <b>NR-15</b>. VOC/NOx em índice, VOC em ppm e formaldeído são apresentados como <b>indicativos</b>, sem equivalência legal direta universal no formato exibido pelo sensor. Aqui usamos a média do período selecionado.
      </Typography>
    </>
  )}
</Paper>
    </Box>
  );
}
